// EmbeddedCheckoutModal — in-dashboard Stripe Embedded Checkout (PE-1572 A).
//
// Renders Stripe's hosted Checkout INSIDE a dashboard modal (no full-page
// redirect): the user picks a paid plan, this fetches a Checkout Session
// client_secret from the pricing backend (pricing.checkout.createEmbedded), and
// mounts <EmbeddedCheckout/>. Stripe handles card entry + SCA/3DS natively; on
// completion its `onComplete` callback fires (redirect_on_completion:'never'),
// we close the modal and ask the parent to refresh so the new plan surfaces once
// the webhook has written it (which can lag a moment).
//
// BRAND CHROME (PE-1572): the modal mirrors the onboarding popups
// (StripeApprovalBanner "ready" dialog, InviteTeamPopup, StripeSuccessScreen) —
// a 48px sage medallion header band, a paper-muted plan/price summary rail
// (same card pattern as the approved dialog's payout card), and a
// "secured by Stripe" footer. The header/rail are DERIVED from `plan` +
// `billingPeriod` via the shared billingPlans presentation source, so the modal
// takes no new props and never hard-codes a price.
//
// SIZING (intentional, native feel): the iframe sits in a tall RESERVED-HEIGHT
// container (.cs-checkout-frame, ~640px) chosen so the common card path fits
// fully WITHOUT the iframe scrolling internally — the modal should read as a
// native page, not a cramped scrollable widget. The reserve also stops the
// modal jumping when Stripe's iframe auto-sizes/shrinks (e.g. a wallet appears,
// or mid-load). overflow-y:auto stays only as a safety net for the rare taller
// path; the modal never grows past max-h-[85dvh] (then the OUTER dialog scrolls,
// never the iframe). One scroll owner: the dialog, not nested boxes.
//
// Stripe libs: the dashboard's declared `@stripe/react-stripe-js` (2.9.0) +
// `@stripe/stripe-js` (4.10.0). DO NOT bump them — v5/v8 belong to
// donation-checkout, which the dashboard must not import. The subscription is on
// the PLATFORM account, so loadStripe takes only the platform publishable key
// (VITE_APP_STRIPE_PK) with NO `stripeAccount`.
//
// Stripe iframe THEMING is NOT done here: Embedded Checkout has no client-side
// `appearance` prop (unlike the Connect onboarding embed). The iframe's button /
// accent color comes from Stripe Dashboard → Settings → Branding (set to sage
// #6B8E6B in test AND live). This chrome frames it; Branding colors the inside.

import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../../../../components/ui/dialog';
import {
  createEmbeddedCheckout,
  type CheckoutPlan,
  type BillingPeriod,
} from '../../../../api/pricing';
import { PLAN_PRESENTATION } from '../../../../utils/billingPlans';
import type { Plan } from '@charitystack/entitlements';
import './billing-plans-page.css';

// loadStripe returns a PROMISE and must be called ONCE at module scope (not per
// render) — re-creating it remounts the Stripe instance. The platform key is a
// public client value (VITE_*); guard a missing key so we render an error
// instead of calling loadStripe(undefined) (which throws inside Stripe).
const STRIPE_PK = import.meta.env.VITE_APP_STRIPE_PK as string | undefined;
const stripePromise: Promise<Stripe | null> | null = STRIPE_PK
  ? loadStripe(STRIPE_PK)
  : null;

// Why the modal closed successfully — drives the parent's toast copy.
//   'paid'               → a real Stripe payment/checkout completed.
//   'already-subscribed' → the org already had a live subscription; nothing was
//                          charged in this flow.
export type CheckoutCompleteReason = 'paid' | 'already-subscribed';

type EmbeddedCheckoutModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // The plan + period to purchase. Null when the modal is closed (no session is
  // fetched). The provider is keyed on these so switching target re-fetches.
  plan: CheckoutPlan | null;
  billingPeriod: BillingPeriod;
  // Called when the modal reaches a terminal success-equivalent state. `reason`
  // tells the parent WHICH so it can toast accurately:
  //   - 'paid': Stripe reported the payment/checkout complete (a real purchase).
  //   - 'already-subscribed': the first-completed-wins guard found a live
  //     subscription, so no session was minted — nothing was paid in THIS flow.
  // Either way the parent closes the modal and refreshes the merchant.
  onComplete: (reason: CheckoutCompleteReason) => void;
  // LEFT COLUMN (PE-1572): the plan-context panel rendered beside the iframe. The
  // parent (BillingPlansPage) builds this as a <PlanCard compact> from the SAME
  // helpers + feeMode the funnel cards use, so price/fee/feature copy can never
  // drift between the billing page and the checkout modal. Null → no left column
  // (the iframe spans full width — e.g. a target we have no presentation for).
  planContext?: ReactNode;
};

// Card / lock glyphs — sage stroke, same icon family as the onboarding popups.
// CardGlyph is EXPORTED so the in-onboarding subscribe step (SubscribeDriver)
// reuses the EXACT same header medallion glyph — one source, so the two checkout
// surfaces can't silently diverge on stroke/path/a11y.
export function CardGlyph() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h4m-7 3h16a2 2 0 002-2V8a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}
function LockGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

export function EmbeddedCheckoutModal({
  open,
  onOpenChange,
  plan,
  billingPeriod,
  onComplete,
  planContext,
}: EmbeddedCheckoutModalProps) {
  // The fetched Checkout Session for the current target, or a non-error
  // "already subscribed" verdict. We fetch BEFORE mounting the provider rather
  // than inside fetchClientSecret, because the PE-1643 first-completed-wins
  // guard (org already has a live subscription → { alreadySubscribed: true },
  // no clientSecret) is NOT a payment failure. Throwing it inside
  // fetchClientSecret would drop the provider into Stripe's internal error UI
  // and never invoke onComplete, leaving the modal stuck on a generic error.
  // Instead we branch on it here and treat it as a (benign) terminal state.
  type SessionState =
    | { status: 'loading' }
    | { status: 'ready'; clientSecret: string }
    | { status: 'already-subscribed' }
    | { status: 'error' };
  const [session, setSession] = useState<SessionState>({ status: 'loading' });

  useEffect(() => {
    // Only fetch when the modal is actually open with a target plan. Reset to
    // loading on every (re)open / target change so a stale verdict from a prior
    // open never leaks into a new attempt.
    if (!open || !plan) {
      setSession({ status: 'loading' });
      return;
    }
    let cancelled = false;
    setSession({ status: 'loading' });
    createEmbeddedCheckout({ plan, billingPeriod })
      .then((result) => {
        if (cancelled) return;
        if (result.alreadySubscribed || !result.clientSecret) {
          setSession({ status: 'already-subscribed' });
        } else {
          setSession({ status: 'ready', clientSecret: result.clientSecret });
        }
      })
      .catch(() => {
        if (!cancelled) setSession({ status: 'error' });
      });
    return () => {
      // Guard against a late resolve writing into a closed/retargeted modal.
      cancelled = true;
    };
  }, [open, plan, billingPeriod]);

  // fetchClientSecret hands the provider the secret we already fetched. The
  // provider only mounts in the 'ready' branch below, so clientSecret is always
  // present here; the guard is a type-narrowing safety net.
  const clientSecret = session.status === 'ready' ? session.clientSecret : null;
  const fetchClientSecret = useCallback(async () => {
    if (!clientSecret) {
      throw new Error('EmbeddedCheckoutModal: no client secret');
    }
    return clientSecret;
  }, [clientSecret]);

  // Stripe's onComplete fires on a real completed checkout — report 'paid'.
  const onStripeComplete = useCallback(() => onComplete('paid'), [onComplete]);
  // The already-subscribed acknowledgement is success-equivalent but NOT a
  // payment — report 'already-subscribed' so the parent toasts accurately.
  const onAlreadySubscribed = useCallback(
    () => onComplete('already-subscribed'),
    [onComplete],
  );

  // Plan name for the header title only — the full plan context (price, fee,
  // features) lives in the left-column `planContext` the parent passes in.
  const planName = plan ? PLAN_PRESENTATION[plan as Plan].name : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Wide two-column modal (820px): header spans full width; below it a plan
          CONTEXT column (left, the parent's <PlanCard compact>) sits beside the
          Stripe iframe (right). The right column gives Stripe's naturally
          thin/tall embed its own width so it fills the space instead of floating
          centered in dead margin. FIXED h-[85dvh]: the modal opens at its max
          height and never resizes — Stripe's auto-sizing iframe used to grow the
          box after load (content-sized → 85dvh jump). The box is overflow-hidden;
          the columns scroll internally (one scroll owner each), never the modal.
          p-0 because each region owns its own padding. */}
      <DialogContent
        // `cs-billing` is intentional: it brings the billing palette TOKENS and
        // the @container context into the Radix portal so the left-column
        // <PlanCard> (whose styles are scoped under .cs-billing) renders correctly
        // outside the page subtree. `.cs-checkout-modal` re-neutralizes the few
        // root properties that would otherwise bleed (min-height/background).
        className="cs-billing cs-checkout-modal flex h-[85dvh] w-full max-w-[820px] flex-col overflow-hidden p-0 gap-0"
        aria-describedby="embedded-checkout-desc"
      >
        {/* HEADER BAND — sage medallion + title (spans both columns). */}
        <div className="cs-checkout-head">
          <div className="cs-checkout-medallion">
            <CardGlyph />
          </div>
          <div className="cs-checkout-head-text">
            <DialogTitle className="cs-checkout-title">
              {planName ? `Upgrade to ${planName}` : 'Complete your upgrade'}
            </DialogTitle>
            {/* sr-only description: no visible sub-line, but Radix wants an
                aria-describedby target — keep one, hidden, non-marketing, so the
                explicit aria-describedby above never dangles. */}
            <DialogDescription id="embedded-checkout-desc" className="sr-only">
              Complete your plan upgrade with Stripe.
            </DialogDescription>
          </div>
        </div>

        {/* TWO-COLUMN BODY. cs-checkout-cols becomes single-column below the
            mobile breakpoint (context stacks above the iframe — see CSS). When
            there's no planContext, the grid collapses to one column and the
            iframe spans full width. */}
        <div className="cs-checkout-cols">
          {/* LEFT — plan context (the parent's compact PlanCard). Omitted → the
              grid is a single iframe column. */}
          {planContext ? (
            <div className="cs-checkout-context">{planContext}</div>
          ) : null}

          {/* RIGHT — the Stripe iframe area. Reserving a tall height keeps the
              modal a consistent size while Stripe's iframe auto-sizes/shrinks;
              overflow-y is a safety net only (the common path never scrolls). */}
          <div className="cs-checkout-body">
            {!stripePromise ? (
              <div className="cs-checkout-state">
                <div className="cs-checkout-state-medallion cs-checkout-state-warn">
                  <LockGlyph />
                </div>
                <p className="cs-checkout-state-text">
                  Checkout isn’t available right now. Please refresh and try
                  again, or contact support.
                </p>
              </div>
            ) : !open || !plan ? null : session.status === 'loading' ? (
              // Session being fetched — spinner inside the reserved frame so the
              // fixed-height container never looks empty/broken mid-load.
              <div className="cs-checkout-frame cs-checkout-frame--centered">
                <span className="cs-checkout-spinner" aria-hidden />
                <p className="cs-checkout-state-text">
                  Preparing secure checkout…
                </p>
              </div>
            ) : session.status === 'error' ? (
              <div className="cs-checkout-frame cs-checkout-frame--centered">
                <div className="cs-checkout-state-medallion cs-checkout-state-warn">
                  <LockGlyph />
                </div>
                <p className="cs-checkout-state-text">
                  We couldn’t start checkout. Please close this and try again, or
                  contact support.
                </p>
              </div>
            ) : session.status === 'already-subscribed' ? (
              // First-completed-wins (PE-1643): the org already has a live
              // subscription. Success-equivalent — notify the parent (which
              // closes + refreshes so the current plan surfaces) rather than
              // erroring.
              <div className="cs-checkout-frame cs-checkout-frame--centered">
                <AlreadySubscribed onAcknowledge={onAlreadySubscribed} />
              </div>
            ) : (
              // 'ready' — mount the Stripe provider. Key on plan+period so a
              // target change re-mounts with the fresh session; unmounting on
              // close tears the iframe down (no stale session lingering).
              <div key={`${plan}-${billingPeriod}`} className="cs-checkout-frame">
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={{ fetchClientSecret, onComplete: onStripeComplete }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-center gap-1.5 border-t border-edge px-4 py-3 text-xs text-ink-muted">
          <LockGlyph />
          <span>Payments secured by Stripe</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Renders the benign already-subscribed state and notifies the parent once.
// Split out so the onComplete notification runs in an effect (a side effect in
// render is unsafe) and fires exactly once per mount of this state.
function AlreadySubscribed({ onAcknowledge }: { onAcknowledge: () => void }) {
  useEffect(() => {
    onAcknowledge();
    // Fire once when this state is reached; re-firing would loop the refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <p className="cs-checkout-state-text">
      You already have an active subscription. Refreshing your plan details…
    </p>
  );
}
